// src/components/documents/DocumentEditor.tsx
import { useState, useEffect } from "react"
import { useDocumentStore } from "@/store/documentStore"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Download, X, Loader2 } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/hooks/use-toast"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"

// === ДИЗАЙН-СЛОЙ ===
import { documentEditorTheme } from "./ui/DocumentEditorUI"

interface DocumentEditorProps {
  docId: string
  onClose: () => void
}

export default function DocumentEditor({ docId, onClose }: DocumentEditorProps) {
  const { documents } = useDocumentStore()
  const documentData = documents.find(d => d.id === docId)

  const [description, setDescription] = useState(documentData?.description || "")
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [viewerUrl, setViewerUrl] = useState("")

  useEffect(() => {
    if (documentData?.fileUrl) {
      const encodedUrl = encodeURIComponent(documentData.fileUrl)
      setViewerUrl(`https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`)
      setIsLoading(false)
    } else {
      setIsLoading(false)
    }
  }, [documentData])

  const handleSaveDescription = async () => {
    if (!documentData) return
    setIsSaving(true)
    try {
      await updateDoc(doc(db, "documents", docId), {
        description: description.trim(),
        updatedAt: new Date(),
      })
      toast({ title: "Описание сохранено" })
    } catch (err) {
      toast({ title: "Ошибка сохранения", variant: "destructive" })
    } finally {
      setIsSaving(false)
    }
  }

  if (!documentData) {
    return <div className="p-10 text-purple-400">Документ не найден</div>
  }

  return (
    <div className={documentEditorTheme.mainContainer}>
      {/* Header */}
      <div className={documentEditorTheme.header}>
        <div className="flex items-center gap-4">
          <div className={documentEditorTheme.headerIcon}>
            <Download className="h-5 w-5 text-white" />
          </div>
          <h2 className={documentEditorTheme.title}>
            {documentData.title}
          </h2>
        </div>

        <div className="flex gap-3">
          <Button 
            onClick={() => window.open(documentData.fileUrl, "_blank")} 
            className={documentEditorTheme.downloadButton}
          >
            <Download className="mr-2 h-4 w-4" />
            Скачать оригинал
          </Button>

          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Боковая панель */}
        <div className={documentEditorTheme.sidebar}>
          <div className="space-y-6">
            <div>
              <div className={documentEditorTheme.sectionLabel}>Статус</div>
              <Badge className={documentEditorTheme.statusBadge}>
                {documentData.status === "approved" ? "Согласован" : 
                 documentData.status === "rejected" ? "Отклонён" : "Ожидает согласования"}
              </Badge>
            </div>

            <div>
              <div className={documentEditorTheme.sectionLabel}>Описание документа</div>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={documentEditorTheme.descriptionTextarea}
                placeholder="Добавьте описание..."
              />
              <Button 
                onClick={handleSaveDescription}
                disabled={isSaving}
                className={documentEditorTheme.saveButton}
              >
                {isSaving ? "Сохранение..." : "Сохранить описание"}
              </Button>
            </div>
          </div>
        </div>

        {/* Просмотр документа */}
        <div className={documentEditorTheme.viewerContainer}>
          {isLoading ? (
            <div className={documentEditorTheme.loadingContainer}>
              <Loader2 className="h-10 w-10 animate-spin text-purple-400" />
            </div>
          ) : viewerUrl ? (
            <div className={documentEditorTheme.iframeContainer}>
              <iframe
                src={viewerUrl}
                className="w-full h-full border-0"
                title={documentData.title}
                allowFullScreen
              />
            </div>
          ) : (
            <div className={documentEditorTheme.noFileMessage}>
              Не удалось загрузить документ
            </div>
          )}
        </div>
      </div>
    </div>
  )
}